import { useCallback, useId, useMemo } from "react";

import { useGlobalSearch } from "./useGlobalSearch";

/**
 * useGlobalSearchParallel - Composes useGlobalSearch for parallel queries.
 *
 * Runs separate queries for people, groups, tags, and (when enablePositions)
 * positions. Results update incrementally. Display is gated by include* flags;
 * queries for people/groups/tags always run (existing behaviour). Positions
 * query only runs when enablePositions is true (feature flag).
 *
 * @param {import("./types").GlobalSearchOptions} options
 */
export const useGlobalSearchParallel = ({
  includePeople = true,
  includeGroups = true,
  includeTags = true,
  includePositions = false,
  enablePositions = false,
  ...options
} = {}) => {
  // Shared across child hooks so Search Results Viewed fires once per mount + term.
  const resultsViewedScope = useId();

  // Intentionally keeping people/groups/tags updated regardless of external filters
  const people = useGlobalSearch({
    ...options,
    resultsViewedScope,
    includePeople: true,
    includeGroups: false,
    includeTags: false,
    includePositions: false,
    includePositionFields: enablePositions,
  });

  const positions = useGlobalSearch({
    ...options,
    resultsViewedScope,
    includePeople: false,
    includeGroups: false,
    includeTags: false,
    includePositions: true,
    includePositionFields: false,
    disabled: options.disabled || !enablePositions,
  });

  const groups = useGlobalSearch({
    ...options,
    resultsViewedScope,
    includePeople: false,
    includeGroups: true,
    includeTags: false,
    includePositions: false,
    includePositionFields: false,
  });

  const tags = useGlobalSearch({
    ...options,
    resultsViewedScope,
    includePeople: false,
    includeGroups: false,
    includeTags: true,
    includePositions: false,
    includePositionFields: false,
  });

  const results = useMemo(() => {
    if (
      !people.results &&
      !positions.results &&
      !groups.results &&
      !tags.results
    ) {
      return null;
    }

    return {
      people: includePeople ? people.results?.people : null,
      positions: includePositions ? positions.results?.positions : null,
      teams: includeGroups ? groups.results?.teams : null,
      tags: includeTags ? tags.results?.tags : null,
      totals: {
        found:
          (includePeople ? (people.results?.people?.counters?.found ?? 0) : 0) +
          (includePositions
            ? (positions.results?.positions?.counters?.found ?? 0)
            : 0) +
          (includeGroups ? (groups.results?.teams?.counters?.found ?? 0) : 0) +
          (includeTags ? (tags.results?.tags?.counters?.found ?? 0) : 0),
        total:
          (includePeople ? (people.results?.people?.counters?.total ?? 0) : 0) +
          (includePositions
            ? (positions.results?.positions?.counters?.total ?? 0)
            : 0) +
          (includeGroups ? (groups.results?.teams?.counters?.total ?? 0) : 0) +
          (includeTags ? (tags.results?.tags?.counters?.total ?? 0) : 0),
      },
    };
  }, [
    includePeople,
    includePositions,
    includeGroups,
    includeTags,
    people.results,
    positions.results,
    groups.results,
    tags.results,
  ]);

  const onLoadMore = useCallback(async () => {
    await Promise.all([
      includePeople ? people.onLoadMore() : Promise.resolve(),
      includePositions ? positions.onLoadMore() : Promise.resolve(),
      includeGroups ? groups.onLoadMore() : Promise.resolve(),
      includeTags ? tags.onLoadMore() : Promise.resolve(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    includeGroups,
    includePeople,
    includePositions,
    includeTags,
    people.onLoadMore,
    positions.onLoadMore,
    groups.onLoadMore,
    tags.onLoadMore,
  ]);

  const loading =
    (includePeople && people.loading) ||
    (includePositions && positions.loading) ||
    (includeGroups && groups.loading) ||
    (includeTags && tags.loading);

  const error = people.error || positions.error || groups.error || tags.error;

  return {
    loading,
    error,
    results,
    onLoadMore,

    loadingPeople: people.loading,
    loadingPositions: positions.loading,
    loadingTeams: groups.loading,
    loadingTags: tags.loading,

    errorPeople: people.error,
    errorPositions: positions.error,
    errorTeams: groups.error,
    errorTags: tags.error,

    resultsPeople: people.results,
    resultsPositions: positions.results,
    resultsTeams: groups.results,
    resultsTags: tags.results,

    onLoadMorePeople: people.onLoadMore,
    onLoadMorePositions: positions.onLoadMore,
    onLoadMoreTeams: groups.onLoadMore,
    onLoadMoreTags: tags.onLoadMore,
  };
};

export default useGlobalSearchParallel;
